// Scoping dei gestori visibili all'utente corrente.
//
// Modello: un gestore normale vede solo il proprio `id_gestore`. Un "super
// gestore" (`is_super_manager` sul suo doc `gestori`) vede in aggiunta i gestori
// elencati in `managed_manager_ids`, ma solo nelle sezioni per cui ha il permesso
// (`super_permissions[section] === true`). Gli admin MBILITY hanno una logica di
// widening propria (basata su `options.admin.*` / VIEW_ALL_DATA) gestita nelle
// singole action: questo helper copre solo il ramo non-admin.

// Sezioni su cui il permesso super può essere concesso, ortogonali ai `modules`
// del gestore (che gestiscono l'accesso ai *propri* dati).
export const SUPER_SECTIONS = ['trips', 'fleet', 'users', 'invoices', 'analytics', 'companies'];

/** Vero se il gestore corrente è configurato come super gestore. */
export const isSuperManager = (currentManager) => currentManager?.is_super_manager === true;

/** Id del gestore a cui l'utente appartiene (own). */
export const getOwnManagerId = (state) => state?.user?.id_gestore || state?.user?.gestore || null;

/**
 * Id dei gestori associati visibili al super nella sezione data. Vuoto se non è
 * super o se non ha il permesso per quella sezione.
 * @param {Object} currentManager - doc gestore dell'utente (state.currentManager)
 * @param {string} section - una delle SUPER_SECTIONS
 */
export function getManagedManagerIds(currentManager, section) {
  if (!isSuperManager(currentManager)) return [];
  if (!currentManager?.super_permissions?.[section]) return [];
  return Array.isArray(currentManager.managed_manager_ids)
    ? currentManager.managed_manager_ids.filter(Boolean)
    : [];
}

/**
 * Id dei gestori che l'utente super può gestire (anagrafica/config): il proprio
 * più tutti gli associati, indipendentemente dai permessi per sezione. Usato per
 * popolare la lista `/gestori` e lo store `state.managers`. Vuoto per non-super.
 */
export function getManageableManagerIds(state) {
  const own = getOwnManagerId(state);
  const ids = new Set();
  if (own) ids.add(own);
  if (isSuperManager(state?.currentManager)) {
    (state.currentManager.managed_manager_ids || []).filter(Boolean).forEach((id) => ids.add(id));
  }
  return [...ids];
}

/**
 * Lista degli id gestore visibili all'utente in una sezione: il proprio più,
 * se super e con permesso, quelli associati. Senza duplicati.
 * @param {Object} state - stato Redux globale
 * @param {string} section - una delle SUPER_SECTIONS
 * @returns {string[]}
 */
export function getVisibleManagerIds(state, section) {
  const own = getOwnManagerId(state);
  const ids = new Set();
  if (own) ids.add(own);
  getManagedManagerIds(state?.currentManager, section).forEach((id) => ids.add(id));
  return [...ids];
}

/** Cardinalità massima di un operatore `in` in Firestore. */
export const FIRESTORE_IN_LIMIT = 30;

/**
 * Prepara un array di id gestore per una `where('id_gestore', 'in', …)`.
 *
 * Firestore rifiuta l'intera query se l'array contiene `undefined`
 * ("Unsupported field value: undefined"), se è vuoto o se supera i 30 valori.
 * Gli array costruiti a mano dalle action ricadono in tutti e tre i casi: un
 * admin MBILITY non ha `id_gestore`, quindi `[...managers, ownId]` porta dentro
 * un `undefined` e fa saltare l'intera vista.
 *
 * @returns {string[]} id validi, deduplicati e limitati; vuoto = non interrogare.
 */
export function toManagerIdsFilter(ids, label = 'query') {
  const clean = [...new Set((ids || []).filter(Boolean))];
  if (clean.length > FIRESTORE_IN_LIMIT) {
    console.warn(
      `${label}: ${clean.length} gestori visibili, la query 'in' ne supporta ${FIRESTORE_IN_LIMIT}`
    );
    return clean.slice(0, FIRESTORE_IN_LIMIT);
  }
  return clean;
}

/**
 * Attivo/disattivo del gestore. È distinto da `deleted` (soft-delete, filtrato
 * a monte da `fetchManagers`): un gestore disattivato resta in anagrafica e nei
 * dati storici, ma non deve essere proposto per nuove assegnazioni. I gestori
 * legacy senza il campo si considerano attivi.
 */
export const isManagerActive = (m) => m?.active !== false;

/**
 * Filtra i gestori per un picker di **assegnazione** mantenendo gli id già
 * selezionati (`keepIds`).
 *
 * Gemello di `selectableDrivers` (driverStatusLogic) e stessa ragione: senza
 * l'eccezione, un gestore disattivato dopo l'assegnazione sparirebbe dalle
 * opzioni, react-select renderebbe il campo vuoto e il primo salvataggio
 * successivo scriverebbe `null`, riassegnando il record in silenzio.
 *
 * Da NON usare sui filtri di lista (storico, analytics, timeline): lì i gestori
 * disattivati devono restare selezionabili, altrimenti i dati passati che li
 * riguardano non sono più filtrabili.
 *
 * Accetta sia i doc gestore (`id`) sia le opzioni già mappate (`value`): alcuni
 * call site tengono in stato la lista di opzioni, non i doc.
 */
export function selectableManagers(list = [], { keepIds = [] } = {}) {
  const keep = new Set((keepIds || []).filter(Boolean).map(String));
  return (list || []).filter(
    (m) =>
      isManagerActive(m) ||
      keep.has(String(m?.id)) ||
      (m?.value != null && keep.has(String(m.value)))
  );
}
